<template>
  <main>
    <section class="error" v-if="error">
      {{ error }}
    </section>

    <div class="action-bar sb-row">
      <section>
        <div class="title">impact report inquiry</div>
      </section>
    </div>

    <section class="no-reports">
      <p>select an event and a kpi, and we’ll show you which <br/>
        selection (brand, reward, day, etc.) has the biggest impact on the selected kpi</p>
    </section>

    <section
      v-if="showEventsList"
      class="inquiry-types">
      <div
        v-for="(eventType, index) in eventDropdown.rows"
        @click="selectEventToGenerateReport(eventType)"
        :key="index"
        class="large-12 columns center-button">
          <a href="#" class="button larger">see which <span class="event-type-label">{{eventType.state.short_label}}</span> has the biggest impact on a kpi</a>
      </div>
    </section>
    <section v-else>
      <div class="dropdown-row">
        <div class="impact-row">
          <section class="content">
            <sb-async-dropdown
              :rows="eventDropdown.rows"
              :selected="eventDropdown.selected"
              :focused.sync="eventDropdown.focused"
              :options="eventDropdown.options"
              @update:selected="updateSelectedEvent"
              class="events-dropdown"
            />
          </section>

          <section class="content">
            <sb-async-dropdown
              :rows="kpisDropdown.rows"
              :selected="kpisDropdown.selected"
              :focused.sync="kpisDropdown.focused"
              :options="kpisDropdown.options"
              @update:selected="updateSelectedKpi"
              class="events-dropdown kpi-selection"
            />
          </section>
        </div>

        <div>
          <section class="action-bar sb-row" style="justify-content: flex-end">
            <div>
              <button
                class="button run-inquiry-button"
                @click="initiateReportJob"
                :disabled="disableRunReport"
              >
                see impact
                <sb-fai class="text-md ml-1" :icon="['fal', 'chevron-right']" />
              </button>
            </div>
          </section>
        </div>
      </div>

      <section v-if="waitingForReport" class="loading-container">
        <h3>inquiry report in progress</h3>
        <p>processing...</p>
        <sb-loading-icon />
      </section>
      <div>
        <sb-2d-table
          class="impact-report"
          :columns="columns"
          :rows="rows"
          :cells="cells"
        >
          <template #column="{ column }">
            {{ generateLabel(column.label) }}
          </template>
          <template #cell="{ row, column }">
            <span class="cell" :class="{ 'with-secondary': cells[row.key][column.key].secondary }">
              <div class="primary">{{cells[row.key][column.key].primary}}</div>
              <div class="secondary">{{cells[row.key][column.key].secondary}}</div>
            </span>
          </template>
        </sb-2d-table>
      </div>
      <div v-if="noRecordsFoundMessage" class="loading-container title">
        no results found
      </div>
    </section>
  </main>
</template>

<script>
import { reportsAPI } from "../../../services/api/reportsAPI";
import { mapState } from "vuex";

export default {
  name: 'InquiryReport',

  data() {
    return {
      columns: [],
      rows: [],
      cells: {},
      error: null,
      showEventsList: true,
      disableRunReport: true,
      reportId: null,
      reportData: {},
      waitingForReport: false,
      noRecordsFoundMessage: false,

      eventDropdown: {
        rows: [],
        selected: null,
        focused: false,
        options: {
          label: (selected) => `see which ${selected.short_label?.toLowerCase()} has the biggest impact on a kpi`
        }
      },
      kpisDropdown: {
        rows: [],
        selected: null,
        focused: false,
        options: {
          label: (selected) => selected ? selected.label?.toLowerCase() : 'select a kpi'
        }
      }
    }
  },

  async created() {
    this.$store.dispatch('LOAD_CURRENT_MERCHANT')

    try {
      await this.fetchEventTypes();
      await this.fetchKpis();
    } catch(err) {
      this.handleError(err);
    }
  },

  computed: {
    ...mapState(['currentMerchant']),

  },

  methods: {
    async fetchEventTypes() {
      const rows = await reportsAPI.fetchInquiryReportEventTypes()
      this.eventDropdown.rows = rows.map((row) => {
        return {
          slot: 'select-row',
          state: row
        }
      })
    },
    async fetchKpis() {
      const rows = await reportsAPI.fetchInquiryReportKpis()
      this.kpisDropdown.rows = rows.map((row) => {
        return {
          slot: 'select-row',
          state: row
        }
      });
    },
    selectEventToGenerateReport(eventType) {
      this.showEventsList = false;
      this.noRecordsFoundMessage = false;
      this.eventDropdown.selected = eventType.state;
    },
    updateSelectedEvent(selectedEvent) {
      this.eventDropdown.selected = { key: selectedEvent.key, short_label: selectedEvent.short_label };
      this.noRecordsFoundMessage = false;
    },
    updateSelectedKpi(selectedKpi) {
      this.kpisDropdown.selected = { key: selectedKpi.key, label: selectedKpi.label };
      this.disableRunReport = false;
    },
    async initiateReportJob() {
      this.waitingForReport = true;
      this.noRecordsFoundMessage = false;
      this.tableReset();

      const options = {
        report: {
          type: 'Reports::ImpactInquiryReport',
          input_data: {
            inquiry_type: this.eventDropdown.selected.key,
            inquiry_kpi: this.kpisDropdown.selected.key,
          }
        }
      };

      const response = await reportsAPI.initializeReportJob(options)

      this.reportId = response.id
      this.pollReport();
    },
    pollReport() {
      setTimeout(() => {
        reportsAPI.fetchReport(this.reportId)
          .then((data) => {
            this.reportData = data.report

            if (data.report.state === 'completed') {
              this.waitingForReport = false;
              if(data.report.output_data.rows) {
                this.setTableData(data.report.output_data);
              } else {
                this.noRecordsFoundMessage = true;
              }
            } else {
              this.pollReport();
            }
          }).catch(this.handleError);
      }, 1000);
    },
    setTableData(data) {
      const rowsAndCells = data.rows.reduce((res, row) => {
        const key = row.label?.toLowerCase().replace(' ', '_');
        res.rows.push({
          label: row.label,
          key
        });

        res.cells[key] = row.values;

        return res;
      }, {
        rows: [],
        cells: {}
      });

      this.columns = data.cols;
      this.rows = rowsAndCells.rows;
      this.cells = rowsAndCells.cells;
    },
    generateLabel(label) {
      return label?.toLowerCase() || 'n/a'
    },
    handleError(e) {
      this.error = e;
      this.waitingForReport = false;
    },
    tableReset() {
      this.columns = [];
      this.rows = [];
      this.cells = {};
    }
  },
}
</script>

<style scoped>
.title {
  margin-bottom: 12px;
  font-size: 20px;
}

.inquiry-title {
  margin-bottom: 12px;
  font-size: 16px;
}

.event-type-label {
  font-family: "AileronLight";
}

.no-reports {
  text-align: center;
}

.button {
  font-family: "AileronThin";
  font-size: 16px;
}

.button.run-inquiry-button:focus {
  background-color: #128ff9;
}

.button.larger {
  padding: 2rem;
  width: 75%
}

.dropdown-row {
  width: fit-content;
}

.impact-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.events-dropdown {
  width: fit-content;
  min-width: 300px;
  margin: 0 0 16px;
  white-space: nowrap;
}

.kpi-selection {
  margin-left: 24px;
}

.events-dropdown >>> .dropdown-item {
  text-transform: lowercase;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.sb-loading-icon {
  width: 100%;
  max-width: 200px;
}

.impact-report {
  align-items: center;
}
.impact-report >>> .column-container {
  margin-left: 50px;
  margin-right: 50px;
}

.sb-2d-table {
  margin-top: 38px;
}

.sb-2d-table .cell {
  min-width: 150px;
}

.sb-2d-table .cell.with-secondary {
  padding: 4px 25px;
}

.sb-2d-table .cell.with-secondary .primary {
  line-height: 18px;
}

.sb-2d-table .cell.with-secondary .secondary {
  line-height: 14px;
  font-size: 0.9em;
}

.run-inquiry-button {
  padding: 13px 30px;
  margin-top: 16px;
}
</style>
